import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import { buildForm, enqueue, isOfflineError } from "@/lib/offline-queue";
import type { Schemas } from "@/types/api";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";

/** Marca um registo que ficou em fila por falta de rede (a UI avisa o segurança). */
export class QueuedOfflineError extends Error {
  constructor() {
    super("Sem rede — registo guardado e será enviado automaticamente.");
    this.name = "QueuedOfflineError";
  }
}

// ─── Tipos ─────────────────────────────────────────────────────────────────────

export type GateEntryTipo = "entrada" | "saida";
export type IncidentSeveridade = "baixa" | "media" | "alta";
export type IncidentStatus = "aberta" | "resolvida";


export interface GateEntryCreatePayload {
  tipo: GateEntryTipo;
  pessoa_ou_veiculo: string;
  motivo?: string | null;
  vehicle_plate?: string | null;
  company?: string | null;
  document_number?: string | null;
  host_id?: string | null;
  /** Enviados num segundo pedido (ver useCreateGateEntry). */
  files?: File[];
}


export const SEVERIDADE_LABELS: Record<IncidentSeveridade, string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
};

// ─── Entradas / saídas ─────────────────────────────────────────────────────────

const GATE_KEY = ["security", "gate-entries"] as const;

/** `day` = "yyyy-MM-dd" (dia civil português). Sem ele vem o histórico todo. */
export const useGateEntries = (tipo?: GateEntryTipo, day?: string) => {
  return useQuery({
    queryKey: [...GATE_KEY, tipo ?? null, day ?? null],
    queryFn: () => api.get("/security/gate-entries", { query: { tipo, day } }),
  });
};

export const useCreateGateEntry = () => {
  return useMutationWithToast({
    mutationFn: async ({ files, ...data }: GateEntryCreatePayload) => {
      try {
        const entry = await api.post("/security/gate-entries", { body: data });

        // Anexos num SEGUNDO pedido: o registo da entrada é o que importa e não
        // pode ficar refém do upload de um ficheiro (nem o endpoint deixa de
        // aceitar JSON, que é o que a fila offline já envia).
        if (files?.length) {
          const form = new FormData();
          for (const f of files) form.append("files", f);
          return await api.upload("/security/gate-entries/{entry_id}/attachments", { path: { entry_id: entry.id }, form });
        }
        return entry;
      } catch (error) {
        // Sem rede (portaria!) → o registo NÃO se perde: vai para a fila local
        // e é enviado assim que houver ligação (ver use-offline-sync).
        if (isOfflineError(error)) {
          await enqueue(
            "gate-entry",
            data as Record<string, unknown>,
            `${data.tipo === "entrada" ? "Entrada" : "Saída"}: ${data.pessoa_ou_veiculo}`,
          );
          throw new QueuedOfflineError();
        }
        throw error;
      }
    },
    invalidateQueries: [GATE_KEY],
    mutationOptions: {
      // `networkMode: "always"` é ESSENCIAL para a fila offline funcionar.
      // Por omissão o React Query usa "online": sem rede, PAUSA a mutation e o
      // `mutationFn` nunca chega a correr — não havia erro para apanhar, o
      // `mutateAsync` ficava pendurado e o registo desaparecia sem ir para a fila.
      // Com "always", o pedido é tentado, falha, e o catch abaixo enfileira-o.
      networkMode: "always"
    },
  });
};

// ─── Ocorrências ───────────────────────────────────────────────────────────────

const INCIDENT_KEY = ["security", "incidents"] as const;

export const useIncidents = (params?: { severidade?: IncidentSeveridade; status?: IncidentStatus }) => {
  return useQuery({
    queryKey: [...INCIDENT_KEY, params?.severidade ?? null, params?.status ?? null],
    queryFn: () => api.get("/security/incidents", { query: { severidade: params?.severidade, status: params?.status } }),
  });
};

export const useCreateIncident = () => {
  return useMutationWithToast({
    mutationFn: async (data: { categoria: string; descricao: string; severidade: IncidentSeveridade; files?: File[] }) => {
      const payload = {
        categoria: data.categoria,
        descricao: data.descricao,
        severidade: data.severidade,
        files: data.files ?? [],
      };
      try {
        return await api.upload("/security/incidents", { form: buildForm(payload) });
      } catch (error) {
        // Idem: a ocorrência E os anexos ficam guardados até haver rede. O
        // IndexedDB guarda `File` nativamente (e um array deles também), por
        // isso as fotos não se perdem.
        if (isOfflineError(error)) {
          await enqueue("incident", payload as unknown as Record<string, unknown>, `Ocorrência: ${data.categoria}`);
          throw new QueuedOfflineError();
        }
        throw error;
      }
    },
    invalidateQueries: [INCIDENT_KEY],
    mutationOptions: {
      // Ver nota em useCreateGateEntry: sem isto o React Query pausa a mutation
      // offline e a ocorrência nunca chegava à fila.
      networkMode: "always"
    },
  });
};

export const useResolveIncident = () => {
  return useMutationWithToast({
    mutationFn: (id: string) =>
      api.patch("/security/incidents/{incident_id}", { path: { incident_id: id }, body: { status: "resolvida" } }),
    invalidateQueries: [INCIDENT_KEY],
  });
};

// ─── Contas de posto (só o superadmin) ─────────────────────────────────────────

export type SecurityAccount = Schemas["SecurityAccountRead"];

const ACCOUNTS_KEY = ["security", "accounts"] as const;

export const useSecurityAccounts = (enabled: boolean) => {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => api.get("/security/accounts"),
    enabled,
  });
};

export const useCreateSecurityAccount = () => {
  return useMutationWithToast({
    mutationFn: (data: { name: string; email: string; password: string }) =>
      api.post("/security/accounts", { body: data }),
    invalidateQueries: [ACCOUNTS_KEY],
  });
};

/** Desativar/reativar. Nunca apagamos: as contas assinam registos e o histórico tem de sobreviver. */
export const useSetSecurityAccountActive = () => {
  return useMutationWithToast({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active
        ? api.post("/security/accounts/{account_id}/activate", { path: { account_id: id } })
        : api.post("/security/accounts/{account_id}/deactivate", { path: { account_id: id } }),
    invalidateQueries: [ACCOUNTS_KEY],
  });
};

export const useResetSecurityAccountPassword = () => {
  return useMutationWithToast({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post("/security/accounts/{account_id}/password", { path: { account_id: id }, body: { password } }),
  });
};
